import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost } from '@app/src/api/fetchHooks'
import { useDrawer } from '@app/src/components/Drawer/DrawerContext'
import type { DrawerViewProps } from '@app/src/components/Drawer/DrawerView'
import DrawerView from '@app/src/components/Drawer/DrawerView'
import DrawerViewDiscardAnswer from '@app/src/components/Drawer/Views/DrawerViewDiscardAnswer'
import { useAuthentication } from '@app/src/context/AuthenticationContext'
import useWriteAccess from '@app/src/hooks/useWriteAccess'
import { AiAnswerBlock } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AiAnswerBlock'
import AnswerSourcesPanel from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AnswerSourcesPanel'
import { Operators } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { palette } from '@app/src/theme/palette'
import type { Provider } from '@app/src/types/organizations'
import type { ScrapedAnswer } from '@app/src/types/resourceExplorer'
import { Solutions } from '@app/src/wf-constants'
import { DeleteOutlined, OpenInNewOutlined } from '@mui/icons-material'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { buildReportingUrl } from './findingsUtils'

type ScrapedAnswerWithResponseItem = ScrapedAnswer & {
  responseItem?: {
    id?: number
    responseId?: number
    response?: { request?: { id?: number } }
    answer?: string
    selectedOptions?: string[]
    requestItem?: { template?: { questionText?: string; kpiName?: string } }
  }
}

type DrawerViewFindingsProps = {
  provider: Provider
  responseItemId: number
} & Omit<DrawerViewProps, 'title' | 'subTitle' | 'buttons'>

const DrawerViewFindings: React.FC<DrawerViewFindingsProps> = ({ provider, responseItemId, ...props }) => {
  const { formatMessage } = useIntl()
  const { openDrawer } = useDrawer()
  const hasWriteAccess = useWriteAccess()
  const { scope } = useAuthentication()
  const isTransparency = scope.solution === Solutions.Transparency

  const payload = useMemo(
    () => ({
      filter: [{ name: 'responseItemId', filters: [{ operator: Operators.EqualTo, value: responseItemId }] }],
      include: [
        'searchResults',
        'responseItem',
        'responseItem.response',
        'responseItem.response.request',
        'responseItem.requestItem',
        'responseItem.requestItem.template',
      ],
    }),
    [responseItemId],
  )

  const { items: scrapedAnswers = [], isLoading } = useFetchCollectionWithPost<ScrapedAnswerWithResponseItem>({
    key: [FetchKey.ScrapedAnswer, 'findings-drawer', responseItemId],
    endpoint: endpoints.scrapedAnswers,
    payload,
    options: { staleTime: 60_000 },
  })

  const finding = scrapedAnswers[0]
  const responseItem = finding?.responseItem
  const requestUrl = finding ? buildReportingUrl(responseItem?.response?.request?.id, responseItem?.responseId) : null
  const discardTarget =
    responseItem?.id && responseItem?.responseId
      ? { responseItemId: responseItem.id, responseId: responseItem.responseId }
      : null

  const questionText =
    responseItem?.requestItem?.template?.kpiName ||
    responseItem?.requestItem?.template?.questionText ||
    formatMessage({ id: 'general.notAvailable' })
  const answerText = responseItem?.selectedOptions?.length
    ? responseItem.selectedOptions.join(', ')
    : (responseItem?.answer ?? '')

  return (
    <DrawerView
      title={formatMessage({ id: 'schemas.responseItem.answerDetail' })}
      subTitle={provider?.name}
      stackButtons
      buttons={[
        ...(requestUrl && !isTransparency
          ? [
              {
                label: formatMessage({ id: 'findings.dialog.viewRequest' }),
                variant: 'text' as const,
                startIcon: <OpenInNewOutlined />,
                to: requestUrl,
              },
            ]
          : []),
        ...(discardTarget && hasWriteAccess && !isTransparency
          ? [
              {
                label: formatMessage({ id: 'reporting.discardAiAnswer.discard' }),
                variant: 'text' as const,
                sx: { color: palette.semantic.error },
                startIcon: <DeleteOutlined sx={{ color: palette.semantic.error }} />,
                onClick: () => openDrawer(<DrawerViewDiscardAnswer {...discardTarget} />),
              },
            ]
          : []),
      ]}
      {...props}
    >
      <Stack pt={1} spacing={2}>
        {isLoading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        )}

        {!isLoading && !finding && (
          <Typography color="text.secondary">{formatMessage({ id: 'findings.empty.findings' })}</Typography>
        )}

        {!isLoading && finding && (
          <Stack spacing={2}>
            <Typography variant="subtitle1" sx={{ px: 3 }}>
              {questionText}
            </Typography>

            <Box mx={3} border={1} borderColor="grey.300" borderRadius={1.5} overflow="hidden">
              <AiAnswerBlock>
                <Typography>{answerText || formatMessage({ id: 'general.notAvailable' })}</Typography>
              </AiAnswerBlock>
              <AnswerSourcesPanel scrapedAnswer={finding} />
            </Box>
          </Stack>
        )}
      </Stack>
    </DrawerView>
  )
}

export default DrawerViewFindings
